import {Injectable} from '@angular/core';
import {AutenticazioneService} from "../core/api/generated";
import {catchError, from, map, Observable, of, switchMap, tap} from "rxjs";
import {PathEnum} from "../shared/enums/path-enum";
import {LocalStorageService} from "./local-storage.service";
import {NavController} from "@ionic/angular/standalone";
import {AuthMode} from "../shared/enums/auth-mode.enum";
import {SpidAuthService} from "./spid-auth.service";
import {PasskeyService} from "./passkey.service";
import {GlobalManagerComuneService} from "./pratiche/global-manager-comune.service";
import {TokenExpiryService} from "./token-expiry.service";
import {LoaderService} from "./loader.service";
import {StudentiMensaService} from "../features/cittadino/mensa/studenti/studenti-mensa.service";
import {PushTokenService} from "./push-token.service";
import {PushContentService} from "./push-content.service";
import {NotificationService} from "./notification.service";

@Injectable({
  providedIn: 'root'
})
export class LogoutManagerService {

  constructor(private localStorageService: LocalStorageService,
              private authService: AutenticazioneService,
              private navCtrl: NavController,
              private spidAuthService: SpidAuthService,
              private passkeyService: PasskeyService,
              private globalManagerComuneService: GlobalManagerComuneService,
              private tokenExpiryService: TokenExpiryService,
              private loaderService: LoaderService,
              private studentiMensaService: StudentiMensaService,
              private pushTokenService: PushTokenService,
              private pushContentService: PushContentService,
              private notificationService: NotificationService) {
  }

  logout() {
    this.tokenExpiryService.stopMonitoring();
    const authMode = this.localStorageService.getAuthMode();
    this.loaderService.showLoader();

    // La rimozione del push token precede `logoutUtente()`: richiede il Bearer del caller, quindi
    // dopo la revoca della sessione risponderebbe 401. Non emette errori — se fallisce, il logout
    // prosegue e il token orfano verrà scartato lato BE al primo invio fallito.
    this.pushTokenService.rimuovi$().pipe(
      switchMap(() => this.authService.logoutUtente()),
      // 200: backend ha revocato sessioni KC + eliminato passkey DB
      switchMap(() => from(this.passkeyService.clearHint().catch(() => {}))),
      switchMap(() => this.localStorageService.cleanPreferenceToken$()),
      switchMap(() => {
        if (authMode === AuthMode.SPID) {
          return from(this.spidAuthService.logout().catch(() => {}));
        }
        return of(null);
      }),
      catchError((error) => {
        if (error?.status === 401) {
          // Token scaduto: pulisci token ma preserva passkey hint
          return this.localStorageService.cleanPreferenceToken$();
        }
        // Altri errori: pulisci tutto
        return from(this.passkeyService.clearHint().catch(() => {})).pipe(
          switchMap(() => this.localStorageService.cleanPreferenceToken$())
        );
      })
    ).subscribe({
      next: () => { this.loaderService.hideLoader(); this.navigateToLogin(); },
      error: () => { this.loaderService.hideLoader(); this.navigateToLogin(); }
    });
  }

  /**
   * Chiude la sessione senza passare dall'endpoint di logout: usato quando è il BE
   * a negare l'accesso (403 sul GET profilo iniziale). Il token è valido ma l'utente
   * non è autorizzato a entrare, quindi va rimosso ogni residuo di sessione e si
   * torna al login — non si può proseguire dentro l'app.
   *
   * A differenza di {@link logout} non chiama `logoutUtente()` (fallirebbe con lo stesso
   * 403) e non mostra il loader: il chiamante è già in un flusso di errore.
   */
  forceLogoutAccessDenied$(): Observable<void> {
    this.tokenExpiryService.stopMonitoring();
    return this.localStorageService.cleanPreferenceToken$().pipe(
      tap(() => this.navigateToLogin()),
      map(() => undefined),
    );
  }

  private navigateToLogin() {
    this.globalManagerComuneService.reset();
    // Wipe dello stato mensa: lista figli, selezione e saldi del contesto precedente
    // non devono sopravvivere al logout (privacy cross-utente). Vedi change reset-mensa-state.
    this.studentiMensaService.reset();
    // Il push token della sessione chiusa non va riusato per il prossimo utente: il guscio nativo
    // lo riconsegnerà al prossimo `appInitEnd`, e sarà registrato per il nuovo caller.
    this.pushTokenService.reset();
    // Notifica push in attesa: può contenere dati personali e non va mostrata al prossimo utente che
    // accede su questo dispositivo. `PushContentService` si azzera già da sé sul segnale di sessione
    // (che copre anche i teardown fuori da qui: 401 nell'interceptor, recovery fallito al bootstrap);
    // il reset esplicito qui rende il wipe evidente accanto agli altri, senza dipendere dall'ordine
    // in cui `cleanPreferenceToken$()` emette.
    this.pushContentService.resetInAttesa();
    // Chiusura degli overlay ancora in scena: `resetInAttesa()` sopra ripulisce lo STORAGE, ma una
    // modale già presentata resta nel DOM, e la navigazione a `/login` qui sotto passa
    // `loggingOut` — che fa bypassare il `ModalGuard`, quindi nessuno la chiuderebbe. Senza questo,
    // la notifica dell'utente che esce resterebbe visibile sopra la pagina di login al prossimo
    // che accede sullo stesso dispositivo. Lo fanno già `TokenExpiryService` e
    // `RefreshTokenInterceptor` per i teardown da token scaduto; qui mancava.
    this.notificationService.closeAll();
    // NB: lo stato del gate dei documenti del Comune NON va ripulito qui. Si azzera da sé in
    // `DocumentiComuneGateService`, in ascolto sul segnale di sessione: quel segnale copre anche i
    // teardown che non passano da questo metodo (401 nell'interceptor, recovery fallito al bootstrap).
    this.localStorageService.removeSlugComune$().subscribe();
    this.localStorageService.removeComuneAttivo$().subscribe();
    // `loggingOut` fa bypassare il ModalGuard (canDeactivate) sulla pagina corrente: senza
    // questo flag, col menu laterale aperto la navigazione a /login verrebbe annullata (vedi
    // ModalGuard). Il logout è un teardown totale e deve navigare sempre.
    this.navCtrl.navigateRoot(PathEnum.LOGIN, {replaceUrl: true, state: {loggingOut: true}}).then(() =>
      console.log('Effettuato logout'));
  }
}
